import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, CheckCircle2, XCircle, Timer, Send, ShieldAlert, Gauge, Printer, CalendarClock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { themedHex, fmtDate } from "@/lib/format";
import StageBadge from "@/components/StageBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Tile({ label, value, sub, color, icon: Icon, glow, testid }) {
  const { theme } = useTheme();
  const c = themedHex(color, theme);
  return (
    <div className={`glass rounded-xl px-4 py-3.5 ${glow || ""}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-mono">{label}</span>
        {Icon && <Icon className="w-4 h-4" style={{ color: c }} />}
      </div>
      <div className="mt-2 font-mono font-medium text-4xl md:text-5xl tracking-tight glow-num tnum" style={{ color: c }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5 font-mono">{sub}</div>}
    </div>
  );
}

function PendingCard({ p }) {
  const max = Math.max(1, ...p.reasons.map((r) => r.count));
  return (
    <section className="glass rounded-xl p-4" data-testid="pending-card">
      <div className="flex items-center gap-2 mb-0.5">
        <CalendarClock className="w-5 h-5 text-orange-400" />
        <h2 className="font-head font-bold text-lg">Pending disposition · follow-up needed</h2>
      </div>
      <p className="text-[11px] text-muted-foreground font-mono mb-3">open referrals waiting on something before a decision · current state (not time-filtered) · not counted as admitted or not admitted</p>
      <div className="grid md:grid-cols-[1fr_2fr] gap-4">
        <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
          <Tile testid="pending-total" label="Pending" value={p.total} color="#F59E0B" sub="awaiting follow-up" />
          <Tile testid="pending-overdue" label="Overdue" value={p.overdue} color="#EF4444" sub="follow-up date passed" />
          <Tile testid="pending-due-today" label="Due today" value={p.due_today} color="#EAB308" sub="follow-up today" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono mb-2">What we're waiting on</div>
          {p.reasons.length === 0 && <p className="text-xs text-muted-foreground font-mono">Nothing pending right now.</p>}
          <div className="space-y-2">
            {p.reasons.map((r) => (
              <div key={r.reason} data-testid={`pending-reason-${r.reason}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>{r.reason}</span><span className="font-mono tnum text-orange-300">{r.count}</span>
                </div>
                <div className="h-2 rounded bg-white/5 overflow-hidden">
                  <div className="h-full bg-orange-400/70" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CaptureTable({ title, rows, labelKey = "key" }) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono mb-2">{title}</div>
      {rows.length === 0 && <p className="text-xs text-muted-foreground font-mono">—</p>}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r[labelKey]} className="text-xs" data-testid={`capture-${r[labelKey]}`}>
            <div className="flex items-center justify-between mb-0.5">
              <span className="truncate mr-2">{r[labelKey]}</span>
              <span className="font-mono tnum text-muted-foreground">
                {r.conversion_rate == null ? "—" : `${r.conversion_rate}%`} · {r.admitted}/{r.total}
              </span>
            </div>
            <div className="h-1.5 rounded bg-white/5 overflow-hidden">
              <div className="h-full bg-emerald-400/70" style={{ width: `${r.conversion_rate || 0}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [period, setPeriod] = useState("fy");
  const [cstart, setCstart] = useState("");
  const [cend, setCend] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async (p = period, s = cstart, e = cend) => {
    if (p === "custom" && !s) return;  // wait until a start date is picked
    setLoading(true);
    try {
      const params = p === "custom" ? { period: p, start: s, end: e || undefined } : { period: p };
      const res = await api.get("/analytics/executive", { params });
      setData(res.data);
    } catch { toast.error("Could not load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const changePeriod = (p) => { setPeriod(p); if (p !== "custom") load(p); };

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/analytics/weekly-digest/send");
      toast.success(`Weekly digest queued to ${r.queued} recipient(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send digest");
    } finally { setSending(false); }
  };

  if (!data) return <div className="p-6 font-mono text-sm text-muted-foreground">Loading analytics…</div>;

  const f = data.funnel;
  const t = data.timing;
  const sla = data.sla;
  const maxVol = Math.max(1, ...data.trend.volume);
  const conv = data.conversion_rate == null ? "—" : `${data.conversion_rate}%`;
  const fmtd = (v) => (v == null ? "—" : `${v}d`);

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight">Executive Analytics</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">
            {data.period_label} · {data.range.start || "—"} → {data.range.end} · <span className="text-emerald-400">conversion, leakage & SLA — PHI-safe</span>
          </p>
          <p className="text-[11px] text-muted-foreground font-mono mt-1 max-w-2xl" data-testid="analytics-basis-note">
            Counting basis: admissions &amp; decisions are counted in the period they <span className="text-cyan-300">occurred</span> (by decision date); placements by referral date; open &amp; at-risk reflect <span className="text-cyan-300">now</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <Select value={period} onValueChange={changePeriod}>
            <SelectTrigger className="w-[150px] bg-slate-900/60 border-white/10 h-9 text-sm" data-testid="analytics-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="fy">This Fiscal Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="custom">Custom range…</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="analytics-custom-range">
              <input type="date" value={cstart} max={cend || undefined}
                     onChange={(e) => { setCstart(e.target.value); load("custom", e.target.value, cend); }}
                     data-testid="analytics-custom-start"
                     className="bg-slate-900/60 border border-white/10 rounded-md h-9 px-2 text-sm text-foreground" />
              <span className="text-muted-foreground text-xs">→</span>
              <input type="date" value={cend} min={cstart || undefined}
                     onChange={(e) => { setCend(e.target.value); load("custom", cstart, e.target.value); }}
                     data-testid="analytics-custom-end"
                     className="bg-slate-900/60 border border-white/10 rounded-md h-9 px-2 text-sm text-foreground" />
            </div>
          )}
          <Button variant="outline" onClick={() => load()} className="gap-2 border-white/15 h-9" data-testid="analytics-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button variant="outline" onClick={() => window.print()} className="gap-2 border-white/15 h-9" data-testid="analytics-print"><Printer className="w-4 h-4" /> Print / PDF</Button>
          {user?.role === "admin" && (
            <Button onClick={sendDigest} disabled={sending} className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold h-9" data-testid="send-digest-btn">
              <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send weekly digest"}
            </Button>
          )}
        </div>
      </div>

      {/* Conversion KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile testid="kpi-conversion" label="Conversion" value={conv} icon={TrendingUp} color="#34D399" glow="glass-violet" sub={`admit ÷ decided · n=${f.decided}`} />
        <Tile testid="kpi-admitted" label="Admitted" value={f.admitted} icon={CheckCircle2} color="#22D3EE" sub="started care" />
        <Tile testid="kpi-notadmitted" label="Not admitted" value={f.not_admitted} icon={XCircle} color="#F87171" sub="leakage" />
        <Tile testid="kpi-confirmrate" label="Confirm rate" value={data.confirm_rate == null ? "—" : `${data.confirm_rate}%`} icon={Gauge} color="#A78BFA" sub={`${f.confirmed} of ${f.total}`} />
        <Tile testid="kpi-ttc" label="Median → confirm" value={fmtd(t.time_to_confirm_median_biz)} icon={Timer} color="#F59E0B" sub={`avg ${fmtd(t.time_to_confirm_avg_biz)} · biz`} />
        <Tile testid="kpi-tto" label="Median → outcome" value={fmtd(t.time_to_outcome_median_biz)} icon={Timer} color="#64748B" sub={`cycle ${fmtd(t.total_cycle_median_biz)}`} />
      </div>

      {/* Funnel + Leakage */}
      <div className="grid md:grid-cols-2 gap-4">
        <section className="glass rounded-xl p-4" data-testid="funnel-card">
          <h2 className="font-head font-bold text-lg mb-0.5">Activity in {data.period_label}</h2>
          <p className="text-[11px] text-muted-foreground font-mono mb-3">each counted when the event occurred</p>
          {(() => {
            const rows = [
              ["Placed (new referrals)", f.total, "#22D3EE"],
              ["Confirmed received", f.confirmed, "#A78BFA"],
              ["Decided (admit + not)", f.decided, "#F59E0B"],
              ["Admitted / started care", f.admitted, "#34D399"],
            ];
            const mx = Math.max(1, ...rows.map((r) => r[1]));
            return rows.map(([label, val, color]) => {
              const pct = Math.round((val / mx) * 100);
              const c = themedHex(color, theme);
              return (
                <div key={label} className="mb-2.5" data-testid={`funnel-${label}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span>{label}</span><span className="font-mono tnum" style={{ color: c }}>{val}</span>
                  </div>
                  <div className="h-3 rounded bg-white/5 overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${pct}%`, background: c }} />
                  </div>
                </div>
              );
            });
          })()}
          <p className="text-xs text-muted-foreground font-mono mt-2">
            Now: {f.awaiting_confirm} awaiting confirmation · {f.awaiting_outcome} awaiting outcome · {f.pending_disposition ?? 0} pending disposition · {f.open} open
          </p>
        </section>

        <section className="glass rounded-xl p-4" data-testid="leakage-card">
          <h2 className="font-head font-bold text-lg mb-3">Why we lost referrals</h2>
          {data.leakage.reasons.length === 0 && <p className="text-xs text-muted-foreground font-mono">No non-admitted referrals in this period.</p>}
          <div className="space-y-2">
            {data.leakage.reasons.map((r) => (
              <div key={r.reason} data-testid={`leak-${r.reason}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>{r.reason}</span><span className="font-mono tnum text-red-300">{r.count} · {r.pct}%</span>
                </div>
                <div className="h-2 rounded bg-white/5 overflow-hidden">
                  <div className="h-full bg-red-400/70" style={{ width: `${r.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
          {data.leakage.reasons.length > 0 && (
            <p className="text-xs text-muted-foreground font-mono mt-3">Total not admitted: {data.leakage.total_not_admitted}</p>
          )}
        </section>
      </div>

      {data.pending && <PendingCard p={data.pending} />}

      {/* SLA / at-risk board */}
      <section className="glass rounded-xl p-4" data-testid="sla-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="w-5 h-5 text-amber-400" />
          <h2 className="font-head font-bold text-lg">SLA &amp; at-risk board</h2>
          <span className="text-xs text-muted-foreground font-mono">confirm ≤ {sla.confirm_target_biz}d · outcome ≤ {sla.outcome_target_biz}d (biz) · pending ≤ follow-up date</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Tile testid="sla-breached" label="Breached" value={sla.breached} icon={ShieldAlert} color="#EF4444" sub="past SLA / stale" />
          <Tile testid="sla-atrisk" label="At risk" value={sla.at_risk} color="#EAB308" sub="approaching SLA" />
          <Tile testid="sla-ontrack" label="On track" value={sla.on_track} color="#34D399" sub={`of ${sla.open_total} open`} />
        </div>
        <div className="overflow-hidden rounded-lg border border-white/8">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono border-b border-white/8">
                <th className="text-left px-3 py-2">ID</th><th className="text-left px-2 py-2">Pt</th>
                <th className="text-left px-2 py-2">Stage</th><th className="text-left px-2 py-2">Lane</th>
                <th className="text-right px-2 py-2">Open</th><th className="text-right px-2 py-2">No note</th>
                <th className="text-left px-3 py-2">Owner</th>
              </tr>
            </thead>
            <tbody>
              {sla.items.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-emerald-400 font-mono text-xs">Nothing breaching or at-risk. 🎯</td></tr>}
              {sla.items.map((it) => (
                <tr key={it.id} className="border-b border-white/5 last:border-0" data-testid={`sla-row-${it.id}`}>
                  <td className="px-3 py-2 font-mono text-cyan-300 text-xs">{it.id}</td>
                  <td className="px-2 py-2 font-mono text-xs">{it.initials}</td>
                  <td className="px-2 py-2"><StageBadge referral={it} /></td>
                  <td className="px-2 py-2 text-xs">{it.lane}</td>
                  <td className={`px-2 py-2 text-right font-mono tnum text-xs ${it.severity === "red" || it.stale ? "text-red-300" : "text-yellow-300"}`} data-testid={`sla-open-${it.id}`}>
                    {it.sla_type === "followup"
                      ? (it.followup_overdue ? `f/u ${it.followup_overdue_days}d overdue` : `f/u ${fmtDate(it.followup_date)}`)
                      : `${it.business_days_open}d`}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tnum text-xs text-muted-foreground">{it.days_since_note}d</td>
                  <td className="px-3 py-2 text-xs truncate">{it.owner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Capture by line/source */}
      <section data-testid="capture-card">
        <h2 className="font-head font-bold text-lg mb-0.5">Conversion by line &amp; source</h2>
        <p className="text-[11px] text-muted-foreground font-mono mb-3">decisions made in {data.period_label} · {"admit ÷ decided · admitted/decided"}</p>
        <div className="grid md:grid-cols-3 gap-3">
          <CaptureTable title="By receiving line" rows={data.by_receiving_line} />
          <CaptureTable title="By source line" rows={data.by_source_line} />
          <CaptureTable title="By referral source" rows={data.by_source} />
        </div>
      </section>

      {/* Trend */}
      <section className="glass rounded-xl p-4" data-testid="trend-card">
        <h2 className="font-head font-bold text-lg mb-3">Volume &amp; conversion trend (8 weeks)</h2>
        <div className="flex items-end gap-2 h-40">
          {data.trend.weeks.map((wk, i) => {
            const vol = data.trend.volume[i];
            const rate = data.trend.conversion[i];
            const h = Math.round((vol / maxVol) * 100);
            return (
              <div key={wk} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`trend-${wk}`}>
                <span className="text-[11px] font-mono text-emerald-300">{rate == null ? "" : `${rate}%`}</span>
                <div className="w-full rounded-t bg-cyan-400/60 transition-all" style={{ height: `${h}%`, minHeight: vol ? 4 : 0 }} title={`${vol} referrals`} />
                <span className="text-[11px] font-mono text-muted-foreground">{wk}</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground font-mono mt-2">Bars = weekly volume · green = conversion %</p>
      </section>
    </div>
  );
}
